import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";

export interface BookmarkDto {
    id: string;
    targetType: "page" | "category";
    targetId: string;
    title: string;
    icon?: string;
    color?: string;
    categoryId?: string;
    categoryName?: string;
    createdAt: string;
}

// module-level state so all hook instances share the same data
let items: BookmarkDto[] = [];
let keys: Set<string> = new Set();
let loading = true;
let initialized = false;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
    listeners.forEach(fn => fn());
}

async function fetchBookmarks() {
    try {
        loading = true;
        notify();
        const res = await axios.get("/api/manuals/bookmarks");
        if (res.data.success) {
            items = res.data.items as BookmarkDto[];
            keys = new Set(items.map(b => `${b.targetType}:${b.targetId}`));
        }
    } catch {
        // silently ignore — sidebar just won't show
    } finally {
        loading = false;
        notify();
    }
}

// module-level reload — safe to call from class components and non-hook contexts
export function reloadBookmarks() {
    fetchBookmarks();
}

export function useBookmarks() {
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        const listener: Listener = () => forceUpdate(n => n + 1);
        listeners.add(listener);

        if (!initialized) {
            initialized = true;
            fetchBookmarks();
        }

        return () => {
            listeners.delete(listener);
        };
    }, []);

    const reload = useCallback(() => {
        fetchBookmarks();
    }, []);

    const toggle = useCallback(async (targetType: "page" | "category", targetId: string): Promise<boolean> => {
        const key = `${targetType}:${targetId}`;
        try {
            if (keys.has(key)) {
                const res = await axios.delete(`/api/manuals/bookmarks/${targetType}/${targetId}`);
                if (res.data.success) {
                    toast("Bookmark removed");
                    await fetchBookmarks();
                    return false;
                }
            } else {
                const res = await axios.post("/api/manuals/bookmarks", { targetType, targetId });
                if (res.data.success) {
                    toast.success("Bookmarked");
                    await fetchBookmarks();
                    return true;
                }
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || "Failed to update bookmark");
        }
        return keys.has(key);
    }, []);

    return { items, keys, loading, reload, toggle };
}
